// LLYLL Newsletter templates — reproduits du mockup v8 en JSX.
// Chaque template lit ses textes depuis tp.body_template.blocks (avec fallback
// aux valeurs par défaut dans touchpoint-blocks.ts). Les variables {prénom}
// sont substituées au render.

import { getBlock, type TpStyle } from "./touchpoint-blocks";

type Touchpoint = {
  id: string;
  number: string;
  style: TpStyle;
  title: string;
  body_text: string | null;
  body_html: string | null;
  body_template?: { blocks?: Record<string, string> } | null;
  video_url: string | null;
  video_thumb_url?: string | null;
  avatar_url?: string | null;
  cta_label: string | null;
  channel: "email" | "whatsapp";
};

type Vars = { prenom: string; sujet1?: string; sujet2?: string; sujet3?: string };

export function TouchpointPreview({
  tp,
  vars,
}: {
  tp: Touchpoint;
  vars: Vars;
}) {
  return (
    <div style={STAGE}>
      <div style={WRAP}>
        {tp.style === "plain" && <TplPlain tp={tp} vars={vars} />}
        {tp.style === "wanted" && <TplWanted tp={tp} vars={vars} />}
        {tp.style === "drama" && <TplDrama tp={tp} vars={vars} />}
        {tp.style === "sticker" && <TplSticker tp={tp} vars={vars} />}
        {tp.style === "info" && <TplInfo tp={tp} vars={vars} />}
        {tp.style === "pop" && <TplPop tp={tp} vars={vars} />}
        {tp.style === "neon" && <TplNeon tp={tp} vars={vars} />}
        {tp.style === "tabloid" && <TplTabloid tp={tp} vars={vars} />}
        {tp.style === "burst" && <TplBurst tp={tp} vars={vars} />}
        {tp.style === "mono" && <TplMono tp={tp} vars={vars} />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Shared tokens & helpers
// ----------------------------------------------------------------------------

const STAGE: React.CSSProperties = {
  background: "#BFBEBA",
  padding: "28px 16px",
  borderRadius: 10,
  minHeight: 480,
};
const WRAP: React.CSSProperties = {
  maxWidth: 460,
  margin: "0 auto",
  fontFamily: "Helvetica Neue, Arial, sans-serif",
  color: "#000",
};

const VAR_HL: React.CSSProperties = {
  background: "#FFFC7D",
  padding: "1px 6px",
  borderRadius: 3,
  fontWeight: 600,
  color: "#000",
};

function sub(text: string, vars: Vars): string {
  return text
    .replace(/\{prénom\}/g, vars.prenom)
    .replace(/\{prenom\}/g, vars.prenom)
    .replace(/\{sujet 1\}/g, vars.sujet1 ?? "")
    .replace(/\{sujet 2\}/g, vars.sujet2 ?? "")
    .replace(/\{sujet 3\}/g, vars.sujet3 ?? "");
}

function withHighlighted(text: string, vars: Vars) {
  // Split on variables and yellow-highlight them in the preview.
  const parts = text.split(/(\{[^}]+\})/g);
  return parts.map((p, i) => {
    if (p.startsWith("{") && p.endsWith("}")) {
      return (
        <span key={i} style={VAR_HL}>
          {sub(p, vars)}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function blocks(tp: Touchpoint) {
  return tp.body_template?.blocks;
}

// ----------------------------------------------------------------------------
// Video card (shared)
// ----------------------------------------------------------------------------

function VideoCard({
  videoUrl,
  thumbUrl,
  avatarUrl,
  metaTop,
  metaSub,
  variant,
  badge,
}: {
  videoUrl: string | null;
  thumbUrl?: string | null;
  avatarUrl?: string | null;
  metaTop: string;
  metaSub: string;
  variant: "wanted" | "drama" | "sticker" | "info" | "pop" | "neon" | "tabloid" | "burst" | "mono";
  badge?: { text: string; bg: string; fg: string; pos: "tl" | "tr" };
}) {
  const playStyle: React.CSSProperties =
    variant === "sticker"
      ? {
          background: "#FFFC7D",
          color: "#000",
          border: "6px solid #000",
          boxShadow: "0 0 0 4px #FFFC7D, 0 0 0 8px #000, 6px 6px 0 8px #000",
        }
      : variant === "info"
        ? {
            background: "#FFFC7D",
            border: "5px solid #000",
            boxShadow: "0 0 0 3px #FFFC7D, 0 0 0 6px #000",
          }
        : {
            background: "#FFFC7D",
            border: "5px solid #000",
            boxShadow: "0 0 0 3px #FFFC7D, 0 0 0 6px #000, 5px 5px 0 6px #000",
          };
  // The PNG dimensions are ~940x1018. The phone in the illustration is tilted
  // ~5° counter-clockwise and the screen takes up these proportions.
  // Calibrated visually from /chief/chief-holding-phone.png — tweak the 4
  // constants below if Jeremy reports misalignment.
  const SCREEN_TOP = "6.5%";
  const SCREEN_LEFT = "11%";
  const SCREEN_WIDTH = "48%";
  const SCREEN_HEIGHT = "62%";
  const SCREEN_ROTATION = "-4deg"; // counter-clockwise to match the drawn phone tilt

  return (
    <a
      href={videoUrl || "#"}
      target={videoUrl ? "_blank" : undefined}
      rel="noopener noreferrer"
      style={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        marginTop: 8,
        maxWidth: 320,
        marginLeft: "auto",
        marginRight: "auto",
        position: "relative",
      }}
      onClick={(e) => {
        if (!videoUrl) e.preventDefault();
      }}
    >
      {/* Container with the Chief-holding-phone illustration as background, +
          the video thumbnail overlaid on the phone screen zone */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "940 / 1018",
        }}
      >
        <img
          src="/chief/chief-holding-phone.png"
          alt="The Chief shows you the video"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
        {/* Phone screen overlay : thumbnail + play button + overlays
            Rotated to match the drawn phone tilt. transform-origin = center
            so the rotation pivot is the middle of the screen. */}
        <div
          style={{
            position: "absolute",
            top: SCREEN_TOP,
            left: SCREEN_LEFT,
            width: SCREEN_WIDTH,
            height: SCREEN_HEIGHT,
            background: "#262522",
            overflow: "hidden",
            zIndex: 2,
            transform: `rotate(${SCREEN_ROTATION})`,
            transformOrigin: "center center",
            borderRadius: "6%",
          }}
        >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt="video thumbnail"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => ((e.currentTarget.style.display = "none"))}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #5a4a3a 0%, #2a1810 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 80, opacity: 0.5 }}>😀</span>
          </div>
        )}
        {badge && (
          <span
            style={{
              position: "absolute",
              top: 14,
              ...(badge.pos === "tl" ? { left: 14 } : { right: 14 }),
              padding: "5px 11px",
              borderRadius: 999,
              border: "2px solid #000",
              fontFamily: "SF Mono, Menlo, monospace",
              fontSize: 10,
              letterSpacing: "0.16em",
              fontWeight: 700,
              textTransform: "uppercase",
              zIndex: 2,
              background: badge.bg,
              color: badge.fg,
            }}
          >
            {badge.text}
          </span>
        )}
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="avatar"
            style={{
              position: "absolute",
              left: 14,
              bottom: 14,
              width: 64,
              height: 64,
              borderRadius: "50%",
              border: "3px solid #D7D2C9",
              boxShadow: "3px 3px 0 #000",
              zIndex: 2,
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              left: 14,
              bottom: 14,
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #d4a884, #7a6553)",
              border: "3px solid #D7D2C9",
              boxShadow: "3px 3px 0 #000",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 22,
              color: "#000",
            }}
          >
            J
          </div>
        )}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 86,
            height: 86,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            ...playStyle,
          }}
        >
          <svg viewBox="0 0 24 24" width={38} height={38} fill="#000">
            <polygon points="7,4 21,12 7,20" />
          </svg>
        </div>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "55%",
            background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.55) 100%)",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      </div>
      </div>
      <div
        style={{
          padding: "14px 4px 0",
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          textAlign: "center" as const,
          color: "#000",
        }}
      >
        {metaTop}
        <small
          style={{
            display: "block",
            fontFamily: "SF Mono, Menlo, monospace",
            fontWeight: 500,
            textTransform: "uppercase",
            fontSize: 10,
            letterSpacing: "0.14em",
            color: "#42413F",
            marginTop: 3,
          }}
        >
          {metaSub}
        </small>
      </div>
    </a>
  );
}

function Signoff({ text }: { text: string }) {
  return (
    <div
      style={{
        textAlign: "center",
        marginTop: 26,
        fontFamily: "SF Mono, Menlo, monospace",
        fontSize: 10,
        letterSpacing: "0.18em",
        color: "#42413F",
        textTransform: "uppercase",
      }}
    >
      {text}
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 1 — Plain text
// ----------------------------------------------------------------------------

function TplPlain({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const bks = blocks(tp);
  const title = getBlock("plain", bks, "title") || tp.title;
  const body = getBlock("plain", bks, "body") || tp.body_text || "";
  const paragraphs = body.split(/\n\n+/).filter(Boolean);
  return (
    <div style={{ background: "white", border: "1px solid #E5E1D8", padding: "32px 28px", borderRadius: 6 }}>
      <div
        style={{
          fontFamily: "SF Mono, Menlo, monospace",
          fontSize: 11,
          color: "#42413F",
          paddingBottom: 8,
          borderBottom: "1px solid #E5E1D8",
          marginBottom: 16,
        }}
      >
        De : jeremy@llyll.com · À : <span style={VAR_HL}>{vars.prenom}</span>@boîte.com
        <br />
        Sujet : {withHighlighted(title, vars)}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.6, color: "#000" }}>
        {paragraphs.map((p, i) => (
          <p key={i} style={{ marginBottom: 10, whiteSpace: "pre-wrap" }}>
            {withHighlighted(p, vars)}
          </p>
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 2 — Wanted
// ----------------------------------------------------------------------------

function TplWanted({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        position: "relative",
        background: "#BFBEBA",
        border: "4px solid #000",
        padding: "36px 24px 30px",
        boxShadow: "8px 8px 0 #000",
      }}
    >
      <Sparks />
      <Slash style={{ top: 32, left: 14 }} />
      <Slash style={{ top: 90, right: 18, transform: "rotate(40deg)" }} />
      <Slash style={{ bottom: 80, left: 20, transform: "rotate(20deg)" }} />
      <Slash style={{ bottom: 140, right: 14, transform: "rotate(-50deg)" }} />
      <span
        style={{
          display: "inline-block",
          padding: "6px 14px",
          background: "#000",
          color: "#D7D2C9",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginBottom: 18,
        }}
      >
        {getBlock("wanted", b, "kicker")}
      </span>
      <div style={{ textAlign: "center", margin: "0 auto 18px", maxWidth: 280 }}>
        <img
          src="/chief/Chief-1.png"
          alt="The Chief"
          style={{ width: "100%", height: "auto", transform: "rotate(-1.5deg)" }}
          onError={(e) => ((e.currentTarget.style.display = "none"))}
        />
      </div>
      <p
        style={{
          textAlign: "center",
          margin: "0 0 22px",
          fontSize: 18,
          fontWeight: 800,
          lineHeight: 1.2,
          letterSpacing: "-0.04em",
        }}
      >
        <strong>{getBlock("wanted", b, "tagline_main")}</strong>
        <span style={{ display: "block", fontSize: 13, letterSpacing: "-0.01em", fontWeight: 600, marginTop: 6 }}>
          {getBlock("wanted", b, "tagline_sub")}{" "}
          <em style={{ fontStyle: "normal", background: "#000", color: "#FFFC7D", padding: "2px 8px" }}>
            {getBlock("wanted", b, "tagline_em")}
          </em>
        </span>
      </p>
      <div
        style={{
          position: "relative",
          background: "#D7D2C9",
          border: "3px solid #000",
          padding: "16px 18px",
          borderRadius: 12,
          fontSize: 14,
          lineHeight: 1.45,
          fontWeight: 500,
        }}
      >
        {withHighlighted(getBlock("wanted", b, "bubble"), vars)}
        <span
          style={{
            position: "absolute",
            left: "24%",
            bottom: -18,
            width: 24,
            height: 20,
            background: "#D7D2C9",
            borderRight: "3px solid #000",
            borderBottom: "3px solid #000",
            transform: "skew(-22deg) rotate(45deg)",
          }}
        />
      </div>
      <div style={{ marginTop: 28 }}>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("wanted", b, "video_meta_top")}
          metaSub={getBlock("wanted", b, "video_meta_sub")}
          variant="wanted"
        />
      </div>
      <Signoff text={getBlock("wanted", b, "signoff")} />
    </div>
  );
}

function Sparks() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: [
          "radial-gradient(circle 3px at 8% 12%, #000 100%, transparent 0)",
          "radial-gradient(circle 2px at 92% 8%, #000 100%, transparent 0)",
          "radial-gradient(circle 4px at 5% 60%, #000 100%, transparent 0)",
          "radial-gradient(circle 2px at 96% 70%, #000 100%, transparent 0)",
          "radial-gradient(circle 3px at 50% 4%, #000 100%, transparent 0)",
          "radial-gradient(circle 2px at 50% 96%, #000 100%, transparent 0)",
        ].join(", "),
      }}
    />
  );
}

function Slash({ style }: { style: React.CSSProperties }) {
  return (
    <span
      style={{
        position: "absolute",
        width: 28,
        height: 4,
        background: "#000",
        transform: "rotate(-30deg)",
        ...style,
      }}
    />
  );
}

// ----------------------------------------------------------------------------
// TPL 3 — Drama
// ----------------------------------------------------------------------------

function TplDrama({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        position: "relative",
        background: "#BFBEBA",
        border: "5px solid #000",
        boxShadow: "10px 10px 0 #000",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: "#000",
          color: "#D7D2C9",
          padding: "10px 16px",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{getBlock("drama", b, "kicker_left")}</span>
        <span style={{ fontFamily: "SF Mono, Menlo, monospace", letterSpacing: "0.1em", fontSize: 10, fontWeight: 500 }}>
          {getBlock("drama", b, "kicker_right")}
        </span>
      </div>
      <div style={{ position: "relative", padding: "24px 22px 8px" }}>
        <div style={{ textAlign: "center", maxWidth: 320, margin: "0 auto" }}>
          <img
            src="/chief/Chief-3.png"
            alt="The Chief"
            style={{ width: "100%", height: "auto", transform: "rotate(2deg) scale(1.02)" }}
            onError={(e) => ((e.currentTarget.style.display = "none"))}
          />
        </div>
      </div>
      <div style={{ background: "#000", color: "#D7D2C9", padding: "18px 22px 20px", textAlign: "center" }}>
        <div style={{ margin: 0, fontSize: 20, lineHeight: 1.2, fontWeight: 800, letterSpacing: "-0.04em" }}>
          {getBlock("drama", b, "tagline_main")}{" "}
          <span style={{ display: "inline-block", background: "#FFFC7D", color: "#000", padding: "2px 10px" }}>
            {getBlock("drama", b, "tagline_em")}
          </span>
        </div>
        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, opacity: 0.92 }}>
          {getBlock("drama", b, "tagline_sub")}
        </div>
      </div>
      <div style={{ background: "#BFBEBA", padding: "24px 22px 24px" }}>
        <div
          style={{
            position: "relative",
            background: "#D7D2C9",
            border: "3px solid #000",
            padding: "14px 18px",
            marginBottom: 28,
            borderRadius: 16,
            fontSize: 14,
            lineHeight: 1.45,
            fontWeight: 500,
          }}
        >
          {withHighlighted(getBlock("drama", b, "bubble"), vars)}
        </div>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("drama", b, "video_meta_top")}
          metaSub={getBlock("drama", b, "video_meta_sub")}
          variant="drama"
          badge={{ text: getBlock("drama", b, "video_badge"), bg: "#000", fg: "#FFFC7D", pos: "tr" }}
        />
        <Signoff text={getBlock("drama", b, "signoff")} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 4 — Sticker
// ----------------------------------------------------------------------------

function TplSticker({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        position: "relative",
        background: "#BFBEBA",
        border: "4px solid #000",
        borderRadius: 28,
        padding: "28px 22px 24px",
        boxShadow: "0 0 0 5px #BFBEBA, 0 0 0 8px #000",
      }}
    >
      <Star style={{ top: 14, right: 24, transform: "rotate(15deg)" }} />
      <Star style={{ bottom: 90, left: 18, transform: "rotate(-20deg)", width: 12, height: 12 }} />
      <div style={{ textAlign: "center" }}>
        <span
          style={{
            display: "inline-block",
            padding: "4px 12px",
            background: "#FFFC7D",
            color: "#000",
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            border: "2px solid #000",
            borderRadius: 999,
            marginBottom: 14,
          }}
        >
          {getBlock("sticker", b, "kicker")}
        </span>
      </div>
      <div style={{ textAlign: "center", maxWidth: 240, margin: "4px auto 14px" }}>
        <div
          style={{
            background: "#D7D2C9",
            border: "4px solid #000",
            borderRadius: "50%",
            aspectRatio: "1/1",
            overflow: "hidden",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <img
            src="/chief/Chief-1.png"
            alt="The Chief"
            style={{ width: "96%", height: "auto", marginBottom: "-2%" }}
            onError={(e) => ((e.currentTarget.style.display = "none"))}
          />
        </div>
      </div>
      <p
        style={{
          textAlign: "center",
          margin: "0 auto 20px",
          fontSize: 18,
          lineHeight: 1.2,
          fontWeight: 800,
          letterSpacing: "-0.04em",
        }}
      >
        <strong>{getBlock("sticker", b, "tagline_main")}</strong>
        <br />
        <em style={{ fontStyle: "normal", background: "#000", color: "#FFFC7D", padding: "1px 8px" }}>
          {getBlock("sticker", b, "tagline_em")}
        </em>{" "}
        {getBlock("sticker", b, "tagline_sub")}
        <small style={{ display: "block", fontSize: 12, fontWeight: 600, marginTop: 6, color: "#262522" }}>
          {getBlock("sticker", b, "tagline_attribution")}
        </small>
      </p>
      <div
        style={{
          position: "relative",
          background: "#FFFC7D",
          border: "3px solid #000",
          padding: "14px 18px",
          margin: "0 6px 24px",
          borderRadius: 20,
          fontSize: 14,
          lineHeight: 1.45,
          fontWeight: 600,
        }}
      >
        {withHighlighted(getBlock("sticker", b, "bubble"), vars)}
      </div>
      <div style={{ margin: "0 6px" }}>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("sticker", b, "video_meta_top")}
          metaSub={getBlock("sticker", b, "video_meta_sub")}
          variant="sticker"
          badge={{ text: getBlock("sticker", b, "video_badge"), bg: "#FFFC7D", fg: "#000", pos: "tl" }}
        />
      </div>
      <Signoff text={getBlock("sticker", b, "signoff")} />
    </div>
  );
}

function Star({ style }: { style: React.CSSProperties }) {
  return (
    <span
      style={{
        position: "absolute",
        width: 16,
        height: 16,
        background: "#000",
        clipPath:
          "polygon(50% 0,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)",
        ...style,
      }}
    />
  );
}

// ----------------------------------------------------------------------------
// TPL 5 — Info
// ----------------------------------------------------------------------------

function TplInfo({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  const s1 = getBlock("info", b, "sujet1");
  const s2 = getBlock("info", b, "sujet2");
  const s3 = getBlock("info", b, "sujet3");
  return (
    <div
      style={{
        position: "relative",
        background: "#BFBEBA",
        border: "3px solid #000",
        padding: "24px 22px",
        borderRadius: 12,
      }}
    >
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          background: "#FFFC7D",
          color: "#000",
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          border: "2px solid #000",
          marginBottom: 12,
        }}
      >
        {getBlock("info", b, "kicker")}
      </span>
      <div style={{ fontSize: 14, marginBottom: 14, lineHeight: 1.5 }}>
        {withHighlighted(getBlock("info", b, "lead_in"), vars)}
      </div>
      <div style={{ fontSize: 14, marginBottom: 18, lineHeight: 1.5 }}>
        {getBlock("info", b, "topics_intro")}{" "}
        <strong>
          <span style={VAR_HL}>{s1}</span>
        </strong>
        ,{" "}
        <strong>
          <span style={VAR_HL}>{s2}</span>
        </strong>{" "}
        et{" "}
        <strong>
          <span style={VAR_HL}>{s3}</span>
        </strong>
        .
      </div>
      <VideoCard
        videoUrl={tp.video_url}
        thumbUrl={tp.video_thumb_url}
        avatarUrl={tp.avatar_url}
        metaTop={getBlock("info", b, "video_meta_top")}
        metaSub={getBlock("info", b, "video_meta_sub")}
        variant="info"
      />
      <div style={{ fontSize: 14, marginTop: 14, lineHeight: 1.5 }}>
        {getBlock("info", b, "question")}
      </div>
      <Signoff text={getBlock("info", b, "signoff")} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 6 — Pop (gradient cyan/magenta, gros impact, pop art vibes)
// ----------------------------------------------------------------------------

function TplPop({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        position: "relative",
        background:
          "linear-gradient(135deg, #FF1493 0%, #FF6B00 50%, #FFFC7D 100%)",
        border: "4px solid #000",
        borderRadius: 18,
        padding: "26px 22px 24px",
        boxShadow: "10px 10px 0 #00E5FF, 14px 14px 0 #000",
        overflow: "hidden",
      }}
    >
      {/* Halftone dots overlay for the pop-art texture */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.15,
          pointerEvents: "none",
          background: [
            "radial-gradient(circle 2px at 8% 14%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 22% 28%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 36% 42%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 50% 56%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 64% 70%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 78% 84%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 92% 12%, #000 100%, transparent 0)",
            "radial-gradient(circle 2px at 14% 88%, #000 100%, transparent 0)",
          ].join(", "),
          backgroundSize: "20px 20px",
        }}
      />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span
            style={{
              display: "inline-block",
              padding: "5px 14px",
              background: "#00E5FF",
              color: "#000",
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              border: "2px solid #000",
              borderRadius: 999,
              boxShadow: "3px 3px 0 #000",
            }}
          >
            {getBlock("pop", b, "kicker")}
          </span>
        </div>

        <h1
          style={{
            margin: "0 0 8px",
            textAlign: "center",
            fontWeight: 900,
            fontSize: 30,
            lineHeight: 1.05,
            letterSpacing: "-0.04em",
            color: "#000",
            textShadow: "2px 2px 0 #FFFC7D, 4px 4px 0 #00E5FF",
            whiteSpace: "pre-line",
          }}
        >
          {sub(getBlock("pop", b, "headline"), vars)}
        </h1>

        <p
          style={{
            margin: "0 0 18px",
            textAlign: "center",
            fontSize: 14,
            fontWeight: 700,
            color: "#000",
            background: "#00E5FF",
            display: "inline-block",
            padding: "3px 10px",
            border: "2px solid #000",
            position: "relative",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {getBlock("pop", b, "tagline_sub")}
        </p>

        <div
          style={{
            background: "#FFFC7D",
            border: "3px solid #000",
            padding: "14px 18px",
            borderRadius: 12,
            fontSize: 14,
            lineHeight: 1.45,
            fontWeight: 600,
            marginBottom: 18,
            boxShadow: "4px 4px 0 #000",
          }}
        >
          {withHighlighted(getBlock("pop", b, "bubble"), vars)}
        </div>

        {/* CTA "button" giant */}
        <a
          href={tp.video_url || "#"}
          target={tp.video_url ? "_blank" : undefined}
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!tp.video_url) e.preventDefault();
          }}
          style={{
            display: "block",
            background: "#000",
            color: "#FFFC7D",
            padding: "16px 18px",
            border: "3px solid #00E5FF",
            borderRadius: 12,
            textAlign: "center",
            fontSize: 18,
            fontWeight: 900,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            textDecoration: "none",
            boxShadow: "5px 5px 0 #FF1493",
            marginBottom: 14,
          }}
        >
          {getBlock("pop", b, "cta_text")}
        </a>

        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("pop", b, "video_meta_top")}
          metaSub={getBlock("pop", b, "video_meta_sub")}
          variant="pop"
        />

        <Signoff text={getBlock("pop", b, "signoff")} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 7 — Neon (cyberpunk dark, glow cyan/magenta)
// ----------------------------------------------------------------------------

function TplNeon({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        position: "relative",
        background: "#050818",
        border: "2px solid #00E5FF",
        borderRadius: 14,
        padding: "24px 22px",
        boxShadow: "0 0 0 4px #050818, 0 0 30px rgba(255, 0, 255, 0.4), 0 0 60px rgba(0, 229, 255, 0.3)",
        overflow: "hidden",
        color: "#E0E0FF",
        fontFamily: "Helvetica Neue, Arial, sans-serif",
      }}
    >
      {/* Grid bg */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(0,229,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.08) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div
          style={{
            fontFamily: "SF Mono, Menlo, monospace",
            fontSize: 10,
            color: "#FF1493",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            marginBottom: 14,
            textShadow: "0 0 8px #FF1493",
          }}
        >
          {getBlock("neon", b, "kicker")}
        </div>
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 28,
            fontWeight: 900,
            letterSpacing: "-0.02em",
            color: "#FFFC7D",
            textShadow: "0 0 12px #00E5FF, 0 0 24px #FF1493",
            whiteSpace: "pre-line",
            lineHeight: 1.1,
          }}
        >
          {sub(getBlock("neon", b, "headline"), vars)}
        </h1>
        <p
          style={{
            margin: "0 0 18px",
            fontFamily: "SF Mono, Menlo, monospace",
            fontSize: 12,
            color: "#39FF14",
            letterSpacing: "0.05em",
            textShadow: "0 0 6px #39FF14",
          }}
        >
          {getBlock("neon", b, "tagline_sub")}
        </p>
        <div
          style={{
            background: "rgba(15, 25, 50, 0.85)",
            border: "1px solid #FF1493",
            borderRadius: 8,
            padding: "14px 18px",
            fontSize: 14,
            lineHeight: 1.5,
            marginBottom: 16,
            boxShadow: "inset 0 0 20px rgba(255, 20, 147, 0.15)",
            color: "#E0E0FF",
          }}
        >
          {withHighlighted(getBlock("neon", b, "bubble"), vars)}
        </div>
        <a
          href={tp.video_url || "#"}
          target={tp.video_url ? "_blank" : undefined}
          rel="noopener noreferrer"
          onClick={(e) => !tp.video_url && e.preventDefault()}
          style={{
            display: "block",
            background: "linear-gradient(90deg, #00E5FF, #FF1493)",
            color: "#050818",
            textAlign: "center",
            padding: "14px 18px",
            fontWeight: 900,
            fontSize: 16,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            textDecoration: "none",
            borderRadius: 6,
            marginBottom: 14,
            boxShadow: "0 0 20px rgba(0, 229, 255, 0.5)",
          }}
        >
          {getBlock("neon", b, "cta_text")}
        </a>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("neon", b, "video_meta_top")}
          metaSub={getBlock("neon", b, "video_meta_sub")}
          variant="neon"
        />
        <div
          style={{
            textAlign: "center",
            marginTop: 18,
            fontFamily: "SF Mono, Menlo, monospace",
            fontSize: 10,
            color: "#00E5FF",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            textShadow: "0 0 6px #00E5FF",
          }}
        >
          {getBlock("neon", b, "signoff")}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 8 — Tabloid (sensational newspaper, red/black/white)
// ----------------------------------------------------------------------------

function TplTabloid({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "3px solid #000",
        padding: 0,
        fontFamily: "Georgia, 'Times New Roman', serif",
        boxShadow: "6px 6px 0 #DC2626",
      }}
    >
      <div
        style={{
          background: "#DC2626",
          color: "#FFF",
          padding: "6px 16px",
          fontFamily: "Helvetica, Arial, sans-serif",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {getBlock("tabloid", b, "kicker")}
      </div>
      <div style={{ padding: "22px 22px 18px" }}>
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 30,
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: "-0.03em",
            color: "#000",
            textTransform: "uppercase",
            fontFamily: "Helvetica, Arial Black, sans-serif",
            whiteSpace: "pre-line",
            textAlign: "center",
          }}
        >
          {sub(getBlock("tabloid", b, "headline"), vars)}
        </h1>
        <p
          style={{
            margin: "8px 0 14px",
            fontSize: 13,
            fontStyle: "italic",
            color: "#42413F",
            textAlign: "center",
            borderBottom: "1px solid #000",
            paddingBottom: 10,
          }}
        >
          {getBlock("tabloid", b, "subhead")}
        </p>
        <p style={{ margin: "0 0 14px", fontSize: 14, lineHeight: 1.55, color: "#000" }}>
          {withHighlighted(getBlock("tabloid", b, "lead"), vars)}
        </p>
        <div
          style={{
            background: "#FFFC7D",
            borderLeft: "5px solid #DC2626",
            padding: "10px 16px",
            margin: "14px 0",
            fontStyle: "italic",
            fontSize: 14,
            lineHeight: 1.4,
            color: "#000",
          }}
        >
          {withHighlighted(getBlock("tabloid", b, "bubble"), vars)}
        </div>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("tabloid", b, "video_meta_top")}
          metaSub={getBlock("tabloid", b, "video_meta_sub")}
          variant="tabloid"
        />
        <div
          style={{
            textAlign: "center",
            marginTop: 16,
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: "0.2em",
            color: "#DC2626",
            textTransform: "uppercase",
          }}
        >
          {getBlock("tabloid", b, "signoff")}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 9 — Burst (Lichtenstein pop art, lemonade explosion + halftone)
// ----------------------------------------------------------------------------

function TplBurst({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  return (
    <div
      style={{
        position: "relative",
        background: "#FFFC7D",
        border: "4px solid #000",
        padding: "30px 22px 24px",
        boxShadow: "8px 8px 0 #000",
        overflow: "hidden",
      }}
    >
      {/* Halftone dots */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.18,
          pointerEvents: "none",
          background:
            "radial-gradient(circle 3px, #000 1px, transparent 2px) 0 0/14px 14px",
        }}
      />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <span
            style={{
              fontFamily: "Helvetica, Arial Black, sans-serif",
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.25em",
              color: "#000",
              background: "#FFF",
              padding: "3px 10px",
              border: "2px solid #000",
              textTransform: "uppercase",
            }}
          >
            {getBlock("burst", b, "kicker")}
          </span>
        </div>

        {/* Burst star with word */}
        <div
          style={{
            position: "relative",
            width: 160,
            height: 160,
            margin: "10px auto 6px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "#FF1493",
              clipPath:
                "polygon(50% 0,58% 30%,90% 25%,72% 50%,100% 60%,68% 65%,80% 100%,50% 78%,20% 100%,32% 65%,0 60%,28% 50%,10% 25%,42% 30%)",
              border: "none",
              filter: "drop-shadow(4px 4px 0 #000)",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Helvetica, Arial Black, sans-serif",
              fontSize: 28,
              fontWeight: 900,
              color: "#FFF",
              textTransform: "uppercase",
              letterSpacing: "-0.02em",
              transform: "rotate(-8deg)",
              textShadow: "2px 2px 0 #000",
            }}
          >
            {getBlock("burst", b, "burst_word")}
          </div>
        </div>

        <h1
          style={{
            textAlign: "center",
            margin: "10px 0 16px",
            fontFamily: "Helvetica, Arial Black, sans-serif",
            fontSize: 26,
            fontWeight: 900,
            color: "#000",
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
            whiteSpace: "pre-line",
            lineHeight: 1.05,
          }}
        >
          {sub(getBlock("burst", b, "headline"), vars)}
        </h1>
        <div
          style={{
            position: "relative",
            background: "#FFF",
            border: "3px solid #000",
            padding: "14px 18px",
            fontSize: 14,
            lineHeight: 1.45,
            fontWeight: 500,
            marginBottom: 18,
            boxShadow: "4px 4px 0 #000",
          }}
        >
          {withHighlighted(getBlock("burst", b, "bubble"), vars)}
        </div>
        <a
          href={tp.video_url || "#"}
          target={tp.video_url ? "_blank" : undefined}
          rel="noopener noreferrer"
          onClick={(e) => !tp.video_url && e.preventDefault()}
          style={{
            display: "block",
            textAlign: "center",
            background: "#000",
            color: "#FFFC7D",
            padding: "12px 18px",
            fontFamily: "Helvetica, Arial Black, sans-serif",
            fontWeight: 900,
            fontSize: 16,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            textDecoration: "none",
            marginBottom: 14,
            border: "3px solid #FFFC7D",
            boxShadow: "0 0 0 3px #000, 5px 5px 0 3px #000",
          }}
        >
          {getBlock("burst", b, "cta_text")}
        </a>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("burst", b, "video_meta_top")}
          metaSub={getBlock("burst", b, "video_meta_sub")}
          variant="burst"
        />
        <div
          style={{
            textAlign: "center",
            marginTop: 16,
            fontFamily: "Helvetica, Arial Black, sans-serif",
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: "0.2em",
            color: "#000",
            textTransform: "uppercase",
          }}
        >
          {getBlock("burst", b, "signoff")}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// TPL 10 — Mono (bauhaus contrast, massive typo, lemonade accents)
// ----------------------------------------------------------------------------

function TplMono({ tp, vars }: { tp: Touchpoint; vars: Vars }) {
  const b = blocks(tp);
  const body = sub(getBlock("mono", b, "body"), vars);
  const paragraphs = body.split(/\n\n+/).filter(Boolean);
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #000",
        padding: 0,
        fontFamily: "Helvetica Neue, Arial, sans-serif",
      }}
    >
      <div
        style={{
          background: "#000",
          color: "#FFFFFF",
          padding: "10px 22px",
          fontFamily: "SF Mono, Menlo, monospace",
          fontSize: 10,
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>{getBlock("mono", b, "kicker")}</span>
        <span>LLYLL</span>
      </div>
      <div style={{ padding: "30px 22px 24px" }}>
        <h1
          style={{
            margin: "0 0 10px",
            fontSize: 30,
            fontWeight: 900,
            color: "#000",
            letterSpacing: "-0.04em",
            lineHeight: 1.0,
            whiteSpace: "pre-line",
          }}
        >
          {sub(getBlock("mono", b, "headline"), vars)}
        </h1>
        <p
          style={{
            margin: "0 0 22px",
            fontSize: 13,
            fontWeight: 300,
            color: "#42413F",
            lineHeight: 1.45,
            borderBottom: "1px solid #000",
            paddingBottom: 14,
          }}
        >
          {getBlock("mono", b, "subhead")}
        </p>
        <div style={{ fontSize: 14, lineHeight: 1.65, color: "#000" }}>
          {paragraphs.map((p, i) => (
            <p key={i} style={{ margin: "0 0 12px", whiteSpace: "pre-wrap" }}>
              {withHighlighted(p, vars)}
            </p>
          ))}
        </div>
        <div
          style={{
            margin: "20px 0",
            background: "#FFFC7D",
            padding: "16px 20px",
            fontStyle: "italic",
            fontSize: 16,
            fontWeight: 500,
            color: "#000",
            borderLeft: "4px solid #000",
            lineHeight: 1.35,
          }}
        >
          {withHighlighted(getBlock("mono", b, "highlight"), vars)}
        </div>
        <VideoCard
          videoUrl={tp.video_url}
          thumbUrl={tp.video_thumb_url}
          avatarUrl={tp.avatar_url}
          metaTop={getBlock("mono", b, "video_meta_top")}
          metaSub={getBlock("mono", b, "video_meta_sub")}
          variant="mono"
        />
        <div
          style={{
            textAlign: "center",
            marginTop: 18,
            fontFamily: "SF Mono, Menlo, monospace",
            fontSize: 10,
            color: "#42413F",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
          }}
        >
          {getBlock("mono", b, "signoff")}
        </div>
      </div>
    </div>
  );
}
